import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Stack, Paper, CircularProgress, Alert, Chip, Button,
  Table, TableHead, TableBody, TableRow, TableCell, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { api } from "../../api";

const STATUS_COLOR = {
  active: "success", trialing: "info", past_due: "warning",
  suspended: "error", canceled: "default", none: "default",
};
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString() : "—"; } catch { return "—"; } };

function StatCard({ label, value, sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 130, flex: 1 }}>
      <Typography variant="h4" fontWeight={800}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

// Діалог ручного керування підпискою одного салону.
function ManageDialog({ row, plans, t, onClose, onApply }) {
  const [planId, setPlanId] = useState(row?.planId ?? "");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!row) return null;

  const apply = async (payload) => {
    setBusy(true); setErr("");
    try {
      // Тариф застосовуємо разом із будь-якою дією (якщо змінився)
      const body = { ...payload };
      if (planId !== "" && planId !== row.planId) body.planId = Number(planId);
      await api.updateAdminBilling(row.workspaceId, body);
      await onApply();
      onClose();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("adminBilling.manageTitle", { name: row.name })}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        {row.provider === "paddle" && (
          <Alert severity="warning" sx={{ mb: 2 }}>{t("adminBilling.paddleManaged")}</Alert>
        )}
        {row.billableMasters > 0 && (
          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            {t("adminBilling.invoiceHint", {
              masters: row.billableMasters,
              rate: row.perMasterRate,
              sum: `${row.suggestedMonthly} ${row.currency}`,
            })}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t("adminBilling.plan")}</Typography>
        <Select fullWidth size="small" value={planId} displayEmpty
          onChange={(e) => setPlanId(e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="">{t("adminBilling.noPlan")}</MenuItem>
          {plans.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.name} · {p.price} {p.currency}/{p.interval === "year" ? t("billing.year") : t("billing.month")}</MenuItem>
          ))}
        </Select>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("adminBilling.markPaidTitle")}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {t("adminBilling.markPaidHint")}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Button variant="contained" size="small" disabled={busy}
            onClick={() => apply({ markPaid: { months: 1 } })}>{t("adminBilling.paidMonth")}</Button>
          <Button variant="contained" size="small" disabled={busy}
            onClick={() => apply({ markPaid: { months: 12 } })}>{t("adminBilling.paidYear")}</Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TextField type="date" size="small" value={until} onChange={(e) => setUntil(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          <Button variant="outlined" size="small" disabled={busy || !until}
            onClick={() => apply({ markPaid: { until } })}>{t("adminBilling.paidUntil")}</Button>
        </Stack>

        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="info" size="small" disabled={busy}
            onClick={() => apply({ status: "trialing" })}>{t("adminBilling.setTrial")}</Button>
          <Button variant="outlined" color="error" size="small" disabled={busy}
            onClick={() => apply({ status: "suspended" })}>{t("adminBilling.suspend")}</Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminBilling() {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], stats: {} });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manage, setManage] = useState(null); // рядок, який керуємо

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [bd, pl] = await Promise.all([api.getAdminBilling(), api.getBillingPlans()]);
      setData(bd);
      setPlans(pl.plans || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = data.stats || {};

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>{t("adminBilling.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("adminBilling.subtitle")}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", gap: 2 }}>
            <StatCard label={t("adminBilling.mrr")} value={`${s.mrr || 0} PLN`} sub={t("adminBilling.mrrSub")} />
            <StatCard label={t("adminBilling.active")} value={s.active || 0} />
            <StatCard label={t("adminBilling.trialing")} value={s.trialing || 0} />
            <StatCard label={t("adminBilling.overdue")} value={s.overdue || 0} />
            <StatCard label={t("adminBilling.total")} value={s.total || 0} />
          </Stack>

          <Paper variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("adminBilling.salon")}</TableCell>
                  <TableCell>{t("adminBilling.status")}</TableCell>
                  <TableCell>{t("adminBilling.plan")}</TableCell>
                  <TableCell>{t("adminBilling.mastersCol")}</TableCell>
                  <TableCell>{t("adminBilling.until")}</TableCell>
                  <TableCell>{t("adminBilling.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={r.workspaceId} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[r.effectiveStatus] || "default"}
                        label={t(`adminBilling.st.${r.effectiveStatus}`, r.effectiveStatus)} />
                    </TableCell>
                    <TableCell>
                      {r.planName ? `${r.planName} · ${r.planPrice} ${r.currency}` : "—"}
                      {r.provider === "paddle" && (
                        <Chip size="small" label="Paddle" sx={{ ml: 0.5 }} variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {/* платні майстри × ставка = сума до рахунку-фактури */}
                      {r.billableMasters > 0 ? (
                        <>
                          {r.billableMasters} × {r.perMasterRate} ={" "}
                          <b>{r.suggestedMonthly} {r.currency}</b>
                        </>
                      ) : "—"}
                    </TableCell>
                    <TableCell>{fmtDate(r.until || r.currentPeriodEnd || r.trialEnd)}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => setManage(r)}>
                        {t("adminBilling.manage")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <ManageDialog row={manage} plans={plans} t={t}
        onClose={() => setManage(null)} onApply={load} />
    </Box>
  );
}
